// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import React from 'react'
import { array, bool, object, oneOfType, string } from 'prop-types'
import Select, { createFilter } from 'react-select'
import { UpdatedMultiSelectWrapper } from './styles'
import UpdatedInputBase from '../UpdatedInputBase'

import {
  ErrorSimple,
  LabelSimple,
  LookupWrapper,
} from 'components/Form'

// NOTE - isClearable will only be a bool.
// this is because react-select handles the clearing
// meaning there is currently no callback which you can
// rely upon to update redux-form outside of this component.

class UpdatedMultiSelect extends UpdatedInputBase {
  static propTypes = {
    autoFocus: bool,
    className: string,
    disabled: bool,
    hideSelectedOptions: bool,
    id: string.isRequired,
    input: object.isRequired,
    isClearable: bool,
    isMulti: bool,
    label: oneOfType([
      string,
      object,
    ]),
    labelKey: string.isRequired,
    lookup: oneOfType([
      string,
      object,
    ]),
    options: array.isRequired,
    valueKey: string.isRequired,
  }

  static defaultProps = {
    className: '',
    hideSelectedOptions: true,
    isMulti: true,
  }

  // we still want to use the inherited handleChange method, however
  // because of a special scenario with react-select, we have to intervene beforehand.
  customOnChange = v => {
    // When using react-select (since v3) and isMulti is true, there is inconsistent behavior
    // when clearing a field (returns an empty array) vs removing all items one at a time. (returns null)
    // https://github.com/JedWatson/react-select/issues/3585
    const value = (
      v === null && this.props.isMulti
        ? []
        : v
    )

    return this.handleChange(value)
  }

  // react-select will include an option's value when filtering
  // so it the input is searching for '1', and there's an option with
  // '1' in its value property, it will include that in the options
  // even though the option's label doesn't necessarily have a '1' in it.
  // this can cause some unexpected behavior for the user:
  // they're searching for '11' (11 GHz band)
  // but it 14 GHz will show because its id is 11.
  filterOption = createFilter({
    matchFrom: 'start',
    stringify: option => option.label,
  })

  // dynamically maps the option label to display
  getOptionLabel = option => option[this.props.labelKey]

  // dynamically maps the option value for component internals and for redux-form
  getOptionValue = option => option[this.props.valueKey]

  render () {
    const {
      autoFocus,
      className,
      disabled,
      hideSelectedOptions,
      input,
      id,
      isClearable,
      isMulti,
      label,
      lookup,
      meta,
      options,
      valueKey,
    } = this.props

    return (
      <UpdatedMultiSelectWrapper className={`updated-multi-select ${className}`}>
        <LabelSimple
          idRoot={id}
          label={label} />
        <Select
          autoFocus={autoFocus}
          className={'input'}
          classNamePrefix={'react-select'}
          isClearable={isClearable}
          isDisabled={disabled}
          filterOption={this.filterOption}
          getOptionLabel={this.getOptionLabel}
          getOptionValue={this.getOptionValue}
          hideSelectedOptions={hideSelectedOptions}
          id={id}
          instanceId={id}
          isMulti={isMulti}
          onChange={this.customOnChange}
          options={options}
          value={input.value}
          valueKey={valueKey} />
        <LookupWrapper
          lookup={lookup} />
        <ErrorSimple
          error={meta.error}
          idRoot={id} />
      </UpdatedMultiSelectWrapper>
    )
  }
}

export default UpdatedMultiSelect
